import { useState, useEffect, useCallback, useRef } from 'react';
import { sanitizeMosTitle } from '@/utils/militaryTranslation';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Send, ExternalLink, CheckCircle2, Loader2, Clock, FileText, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DISCLAIMER = 'This starts a tracked Sergeant TAP application. The SkillBridge provider (and your command) control acceptance and eligibility — Sergeant TAP is not the DoD approval authority.';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

// SkillBridge tracked-application CTA + workflow. Replaces the old dead-end
// "Contact Provider" button. Reuses the existing Sergeant TAP application
// lifecycle via /api/skillbridge/* endpoints.
export function SkillBridgeApply({ job, user, onApplyOpenChange }) {
  const navigate = useNavigate();
  const isAuthed = !!user;
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const dialogContentRef = useRef(null);

  // Toggle the apply dialog AND notify the containing Opportunity Preview so it can suspend
  // itself on smaller screens (the drawer sits above the dialog otherwise). Mirrors the
  // native Apply flow's onApplyOpenChange wiring.
  const setDialog = useCallback((open) => {
    setDialogOpen(open);
    onApplyOpenChange?.(open);
  }, [onApplyOpenChange]);

  const loadContext = useCallback(async () => {
    if (!isAuthed) { setLoading(false); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/skillbridge/apply-context/${job.job_id}`, { withCredentials: true });
      setCtx(res.data);
      setApp(res.data.existing_application || null);
    } catch (err) {
      // Non-fatal: fall back to a sign-in / unavailable state.
    } finally {
      setLoading(false);
    }
  }, [isAuthed, job.job_id]);

  useEffect(() => { loadContext(); }, [loadContext]);

  const channel = ctx?.channel;
  const provider = ctx?.opportunity?.provider || job.employer_name || 'the provider';

  const handleSendEmail = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/skillbridge/applications/send-email`,
        { job_id: job.job_id, message: message.trim() || null }, { withCredentials: true });
      setApp(res.data.application);
      setDialog(false);
      toast.success(res.data.already_applied ? 'You have already applied to this opportunity' : 'Application sent — tracked in your transition');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send your application. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleStart = async (ch, openUrl) => {
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/skillbridge/applications/start`,
        { job_id: job.job_id, channel: ch }, { withCredentials: true });
      setApp(res.data.application);
      setDialog(false);
      if (openUrl) window.open(openUrl, '_blank', 'noopener,noreferrer');
      toast.success('Application started — tracked in your transition');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start application. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleStatus = async (status) => {
    setBusy(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/skillbridge/applications/${app.application_id}/status`,
        { status }, { withCredentials: true });
      setApp(res.data.application);
      toast.success(status === 'applied' ? 'Marked as applied' : 'Marked as contacted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-2" data-testid="sb-apply-loading">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  // ── Not authenticated ──
  if (!isAuthed) {
    return (
      <div className="text-center" data-testid="skillbridge-action-section">
        <Button
          data-testid="sign-in-to-start-btn"
          onClick={() => navigate('/auth')}
          className="w-full h-12 bg-brand hover:bg-brand-hover text-white"
        >
          Sign in to start a SkillBridge application
        </Button>
        <p className="text-xs text-slate-400 mt-2">{DISCLAIMER}</p>
      </div>
    );
  }

  // ── Existing tracked application ──
  if (app) {
    const status = app.status;
    const isApplied = status === 'applied';
    const isContacted = status === 'contacted';
    return (
      <div data-testid="skillbridge-action-section">
        <div
          className={`flex items-start gap-3 p-4 rounded-xl border ${isApplied ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
          data-testid="sb-application-status"
        >
          {isApplied ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          ) : (
            <Clock className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${isApplied ? 'text-emerald-800' : 'text-slate-800'}`} data-testid="sb-application-status-label">
              {isApplied
                ? `Applied${app.submitted_at || app.applied_at ? ` ${fmtDate(app.submitted_at || app.applied_at)}` : ''}`
                : isContacted ? 'Contacted provider' : 'Application started'}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {isApplied
                ? `Your application to ${provider} is tracked in your transition.`
                : `You're pursuing this opportunity with ${provider}. Update the status as you go.`}
            </p>
            {app.provider_url && (
              <a
                href={app.provider_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-brand hover:underline mt-2"
                data-testid="sb-application-provider-link"
              >
                Visit provider site <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>

        {!isApplied && (
          <div className="flex flex-col sm:flex-row gap-3 mt-3">
            {app.application_method === 'contact_provider' && !isContacted && (
              <Button
                variant="outline"
                onClick={() => handleStatus('contacted')}
                disabled={busy}
                className="flex-1 border-slate-300 text-slate-700"
                data-testid="sb-apply-mark-contacted-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Mark as contacted
              </Button>
            )}
            <Button
              onClick={() => handleStatus('applied')}
              disabled={busy}
              className="flex-1 bg-brand hover:bg-brand-hover text-white"
              data-testid="sb-apply-mark-applied-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Mark as applied
            </Button>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3 text-center">{DISCLAIMER}</p>
      </div>
    );
  }

  // ── No application yet: primary Start CTA ──
  const startLabel = 'Start SkillBridge Application';

  return (
    <div className="text-center" data-testid="skillbridge-action-section">
      <Button
        onClick={() => { if (dialogOpen || busy) return; setMessage(''); setDialog(true); }}
        className="w-full h-12 bg-brand hover:bg-brand-hover text-white"
        data-testid="skillbridge-start-application-btn"
      >
        <Send className="w-5 h-5 mr-2" />
        {startLabel}
      </Button>
      <p className="text-xs text-slate-400 mt-2">{DISCLAIMER}</p>

      <Dialog open={dialogOpen} onOpenChange={(o) => !busy && setDialog(o)}>
        <DialogContent
          ref={dialogContentRef}
          className="max-w-lg max-h-[90vh] overflow-y-auto text-left"
          data-testid="sb-apply-dialog"
          onOpenAutoFocus={(e) => {
            // Mobile fix: prevent Radix from autofocusing the first field (the optional
            // Personal note Textarea), which pops the on-screen keyboard before the user
            // can even see the modal. Move initial focus to the non-input dialog container.
            e.preventDefault();
            dialogContentRef.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">{startLabel}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {channel === 'provider_email' && `Review your details, then we'll email your interest to ${ctx?.provider?.recipient_display || provider}.`}
              {channel === 'provider_site' && `We'll track this application and open ${provider}'s official site so you can apply there.`}
              {channel === 'contact_provider' && `We'll track your outreach to ${provider}.`}
            </DialogDescription>
          </DialogHeader>

          <p className="text-sm text-slate-500 -mt-1" data-testid="sb-apply-context-line">
            Applying to: <span className="font-medium text-slate-700">{job.title}</span>
            {provider ? <> · {provider}</> : null}
          </p>

          {/* PROVIDER EMAIL: review + send */}
          {channel === 'provider_email' && (
            <div className="space-y-4 mt-2">
              <PrefillBlock prefill={ctx?.prefill} resume={ctx?.resume} navigate={navigate} />
              <div>
                <Label className="text-slate-700 text-sm">Personal note <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add anything you'd like the provider to know…"
                  className="mt-1.5 min-h-[100px]"
                  data-testid="sb-apply-message-input"
                />
              </div>
              <p className="text-xs text-slate-400">{DISCLAIMER}</p>
            </div>
          )}

          {/* PROVIDER SITE */}
          {channel === 'provider_site' && (
            <div className="space-y-4 mt-2">
              <PrefillBlock prefill={ctx?.prefill} resume={ctx?.resume} navigate={navigate} compact />
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600">
                Applications for this opportunity are submitted on the provider's own site. We'll mark this as
                <span className="font-medium text-slate-800"> started</span> and open it in a new tab. When you've applied, come back and mark it as applied.
              </div>
              <p className="text-xs text-slate-400">{DISCLAIMER}</p>
            </div>
          )}

          {/* CONTACT ONLY */}
          {channel === 'contact_provider' && (
            <div className="space-y-4 mt-2">
              <PrefillBlock prefill={ctx?.prefill} resume={ctx?.resume} navigate={navigate} compact />
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600" data-testid="sb-apply-contact-details">
                <div className="flex items-center gap-2 text-slate-800 font-medium">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  {ctx?.provider?.contact_name || provider}
                </div>
                <p className="mt-1 text-xs">Reach out to the provider directly, then track your progress here.</p>
              </div>
              <p className="text-xs text-slate-400">{DISCLAIMER}</p>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialog(false)} disabled={busy} className="border-slate-300" data-testid="sb-apply-cancel-btn">
              Cancel
            </Button>
            {channel === 'provider_email' && (
              <Button onClick={handleSendEmail} disabled={busy} className="bg-brand hover:bg-brand-hover text-white" data-testid="sb-apply-send-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send application
              </Button>
            )}
            {channel === 'provider_site' && (
              <Button onClick={() => handleStart('provider_site', ctx?.provider?.url)} disabled={busy} className="bg-brand hover:bg-brand-hover text-white" data-testid="sb-apply-open-site-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Start & open provider site
              </Button>
            )}
            {channel === 'contact_provider' && (
              <Button onClick={() => handleStart('contact_provider', null)} disabled={busy} className="bg-brand hover:bg-brand-hover text-white" data-testid="sb-apply-start-contact-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Start tracking
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PrefillBlock({ prefill, resume, navigate, compact }) {
  if (!prefill) return null;
  const rows = [
    ['Name', prefill.name],
    ['Email', prefill.email],
    ['Phone', prefill.phone],
    ['Service', [prefill.rank, prefill.branch].filter(Boolean).join(' ')],
    ['Occupation', [prefill.mos_code, sanitizeMosTitle(prefill.mos_title, prefill.rank)].filter(Boolean).join(' — ')],
    ['Years of service', prefill.years_of_service ? String(prefill.years_of_service) : ''],
    ['Transition date', prefill.transition_date],
  ].filter(([, v]) => v);

  return (
    <div className="p-3 bg-brand-light/60 rounded-lg border border-brand/15" data-testid="sb-apply-prefill">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">From your Sergeant TAP profile</p>
      <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'} gap-x-4 gap-y-1.5`}>
        {rows.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <span className="text-[11px] text-slate-400">{label}</span>
            <p className="text-sm text-slate-800 truncate">{value}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2.5 text-xs text-slate-500">
        <FileText className="w-3.5 h-3.5 text-slate-400" />
        {resume?.is_complete
          ? <span data-testid="sb-apply-resume-note">Your Sergeant TAP resume will be included.</span>
          : (
            <button type="button" onClick={() => navigate('/resume-builder')} className="underline hover:text-brand" data-testid="sb-apply-resume-cta">
              Add a resume to strengthen your application
            </button>
          )}
      </div>
    </div>
  );
}

export default SkillBridgeApply;
